import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MicButton } from "@/components/MicButton";
import { PaperPlaneRight, Camera, LinkSimple, X, Trash, ChatsCircle, UsersThree, Briefcase, CircleNotch, Check, Checks, PushPin, PushPinSlash } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const DEFAULT_REPLIES = ["On my way", "Job done", "Call me", "Running late", "Need parts"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const linkHref = (m) => (m.link_type === "customer" ? `/customers?focus=${m.link_id}` : `/messages?link_type=job&link_id=${m.link_id}&label=${encodeURIComponent(m.link_label)}`);

const ReadTick = ({ m, reads }) => {
  const seen = reads.filter((r) => r.last_read >= m.created_at).map((r) => r.user_name);
  return (
    <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${seen.length ? "text-brand-gold" : "text-slate-400"}`} title={seen.length ? `Seen by ${seen.join(", ")}` : "Sent"} data-testid={`team-msg-read-${m.id}`}>
      {seen.length ? <><Checks size={14} weight="bold" /><span>Seen{seen.length ? ` by ${seen.join(", ")}` : ""}</span></> : <><Check size={14} weight="bold" /><span>Sent</span></>}
    </div>
  );
};

const PinnedBar = ({ pinned, onUnpin }) => !pinned.length ? null : (
  <div className="mb-2 border border-brand-gold bg-brand-gold/15 max-h-36 overflow-y-auto" data-testid="pinned-bar">
    {pinned.map((m) => (
      <div key={m.id} className="flex items-start gap-2 px-3 py-2 text-sm border-b border-brand-gold/30 last:border-0" data-testid={`pinned-${m.id}`}>
        <PushPin size={16} weight="fill" className="text-brand-navy shrink-0 mt-0.5" />
        <button type="button" className="flex-1 text-left min-w-0" onClick={() => { const el = document.querySelector(`[data-testid="team-msg-${m.id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); else toast.info("That message is older than the chat history shown here"); }} data-testid={`pinned-jump-${m.id}`}>
          <span className="block truncate"><b>{m.user_name}:</b> {m.text || "Photo"}</span>
          <span className="block text-[11px] text-muted-foreground">Pinned by {m.pinned_by} · {fmt(m.pinned_at)}</span>
        </button>
        <button type="button" onClick={() => onUnpin(m)} title="Unpin" data-testid={`pinned-unpin-${m.id}`} className="-m-2 p-2 opacity-60 hover:opacity-100"><PushPinSlash size={16} /></button>
      </div>
    ))}
  </div>
);

const Bubble = ({ m, mine, onDelete, reads, isPinned, onPin }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`team-msg-${m.id}`}>
    <div className={`max-w-[85%] sm:max-w-[70%] px-4 py-2.5 ${mine ? "bg-brand-navy text-white" : "bg-white border border-border"} ${isPinned ? "ring-2 ring-brand-gold" : ""}`}>
      <div className={`text-[11px] mb-1 flex gap-2 ${mine ? "text-slate-300" : "text-muted-foreground"}`}><b>{mine ? "You" : m.user_name}</b><span>{fmt(m.created_at)}</span>
        <button type="button" onClick={() => onPin(m, !isPinned)} className={`ml-auto -my-2 p-2 ${isPinned ? "text-brand-gold" : "opacity-60 hover:opacity-100"}`} title={isPinned ? "Unpin" : "Pin to top"} data-testid={`team-msg-pin-${m.id}`}><PushPin size={14} weight={isPinned ? "fill" : "regular"} /></button>
        {mine && <button type="button" onClick={() => onDelete(m)} className="-my-2 -mr-2 p-2 opacity-60 hover:opacity-100" title="Delete" data-testid={`team-msg-delete-${m.id}`}><Trash size={14} /></button>}
      </div>
      {m.photo_url && <a href={`${BASE}${m.photo_url}`} target="_blank" rel="noreferrer"><img src={`${BASE}${m.photo_url}`} alt="" className="max-h-64 mb-1.5 border border-white/20" /></a>}
      {m.text && <div className="whitespace-pre-wrap break-words text-sm">{m.text}</div>}
      {m.link_type && (
        <Link to={linkHref(m)} className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 ${mine ? "bg-white/15 text-brand-gold" : "bg-brand-gold/25 text-brand-navy"}`} data-testid={`team-msg-link-${m.id}`}>
          {m.link_type === "customer" ? <UsersThree size={12} /> : <Briefcase size={12} />}{m.link_label}
        </Link>
      )}
      {mine && <ReadTick m={m} reads={reads} />}
    </div>
  </div>
);

const LinkPicker = ({ onPick, onClose }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ customers: [], jobs: [] });
  useEffect(() => {
    if (q.trim().length < 2) { setRes({ customers: [], jobs: [] }); return; }
    const t = setTimeout(() => { api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const row = (type, id, label) => (
    <button key={`${type}-${id}`} type="button" onClick={() => onPick({ link_type: type, link_id: id, link_label: label })} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2" data-testid={`link-pick-${type}-${id}`}>
      {type === "customer" ? <UsersThree size={14} /> : <Briefcase size={14} />}{label}
    </button>
  );
  return (
    <div className="border border-border bg-white mb-2" data-testid="link-picker">
      <div className="flex items-center gap-2 p-2 border-b"><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer or job…" className="h-9 rounded-none" data-testid="link-picker-search" /><Button type="button" variant="ghost" size="sm" onClick={onClose}><X size={16} /></Button></div>
      <div className="max-h-48 overflow-y-auto">
        {(res.customers || []).map((c) => row("customer", c.id, c.name))}
        {(res.jobs || []).map((j) => row("job", j.id, `${j.job_number || "Job"} — ${j.customer_name || ""}`))}
      </div>
    </div>
  );
};

const Composer = ({ onSent, presetLink, replies }) => {
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [link, setLink] = useState(presetLink || null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); setPhoto((await api.post("/team-messages/photo", fd)).data.url); }
    catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail) || "Photo upload failed"); }
    setBusy(false);
  };
  const send = async (quick) => {
    const body = typeof quick === "string" ? quick : text;
    if (!body.trim() && !photo) return;
    setBusy(true);
    try {
      const { data } = await api.post("/team-messages", { text: body, photo_url: photo, ...(link || {}) });
      if (typeof quick !== "string") setText("");
      setPhoto(""); if (!presetLink) setLink(null);
      onSent(data);
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="border-t border-border bg-card p-3" data-testid="team-composer">
      {picking && <LinkPicker onPick={(l) => { setLink(l); setPicking(false); }} onClose={() => setPicking(false)} />}
      {(photo || link) && (
        <div className="flex flex-wrap gap-2 mb-2 text-xs">
          {photo && <span className="inline-flex items-center gap-1.5 border px-2 py-1"><img src={`${BASE}${photo}`} alt="" className="h-8 w-8 object-cover" />Photo<button type="button" onClick={() => setPhoto("")}><X size={12} /></button></span>}
          {link && <span className="inline-flex items-center gap-1 bg-brand-gold/25 text-brand-navy px-2 py-1 font-semibold" data-testid="composer-link-chip">{link.link_label}{!presetLink && <button type="button" onClick={() => setLink(null)}><X size={12} /></button>}</span>}
        </div>
      )}
      <div className="flex gap-1.5 mb-2 overflow-x-auto pb-0.5" data-testid="quick-replies">
        {replies.map((q, i) => (
          <button key={`${i}-${q}`} type="button" disabled={busy} onClick={() => send(q)} className="shrink-0 px-3 py-1.5 text-xs font-semibold border border-brand-navy/30 text-brand-navy bg-white hover:bg-brand-navy hover:text-white transition-colors disabled:opacity-50" data-testid={`quick-reply-${slug(q)}`}>{q}</button>
        ))}
      </div>
      <div className="flex flex-wrap sm:flex-nowrap gap-2 items-end">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="team-photo-input" />
        <div className="flex gap-2 order-2 sm:order-none">
        <button type="button" onClick={() => fileRef.current?.click()} className="h-11 w-11 shrink-0 inline-flex items-center justify-center border border-border text-brand-navy hover:bg-secondary" title="Send a photo" data-testid="team-photo-btn"><Camera size={20} /></button>
        {!presetLink && <button type="button" onClick={() => setPicking((v) => !v)} className="h-11 w-11 shrink-0 inline-flex items-center justify-center border border-border text-brand-navy hover:bg-secondary" title="Link to a customer or job" data-testid="team-link-btn"><LinkSimple size={20} /></button>}
        <MicButton onText={(t) => setText((s) => (s ? `${s} ${t}` : t))} testid="team-mic-btn" />
        </div>
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 768) { e.preventDefault(); send(); } }}
          placeholder="Type a message…" className="rounded-none min-h-[44px] max-h-40 flex-1 basis-full sm:basis-auto order-1 sm:order-none" data-testid="team-text-input" />
        <Button onClick={() => send()} disabled={busy} className="h-11 rounded-none bg-brand-navy hover:bg-brand-navy/90 text-white order-3 sm:order-none ml-auto sm:ml-0" data-testid="team-send-btn">{busy ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneRight size={18} weight="fill" />}</Button>
      </div>
    </div>
  );
};

export function TeamChat({ lt = "", lid = "", llabel = "", onShowAll, className = "", header }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [reads, setReads] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [replies, setReplies] = useState(DEFAULT_REPLIES);
  const endRef = useRef(null);
  const add = useCallback((list) => setMsgs((cur) => {
    const seen = new Set(cur.map((m) => m.id));
    return [...cur, ...list.filter((m) => !seen.has(m.id))];
  }), []);
  useEffect(() => {
    setMsgs([]);
    api.get("/team-messages", { params: { link_type: lt, link_id: lid } }).then((r) => setMsgs(r.data)).catch(() => {});
  }, [lt, lid]);
  useEffect(() => {
    const load = () => {
      api.get("/team-messages/reads").then((r) => setReads(r.data)).catch(() => {});
      api.get("/team-messages/pinned").then((r) => setPinned(r.data)).catch(() => {});
    };
    load();
    api.get("/business").then((r) => { if (Array.isArray(r.data.quick_replies)) setReplies(r.data.quick_replies.filter(Boolean)); }).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => {
      setMsgs((cur) => {
        const after = cur.length ? cur[cur.length - 1].created_at : "";
        api.get("/team-messages", { params: { after, link_type: lt, link_id: lid } }).then((r) => r.data.length && add(r.data)).catch(() => {});
        return cur;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [lt, lid, add]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
    if (!msgs.length) return;
    const last = msgs[msgs.length - 1].created_at;
    localStorage.setItem("tm_last_read", last);
    if (!lt && document.visibilityState === "visible") { api.post("/team-messages/read", { at: last }).catch(() => {}); }
  }, [msgs, lt]);
  const pin = async (m, on) => {
    try {
      const { data } = await api.post(`/team-messages/${m.id}/pin`, { pinned: on });
      setPinned((cur) => (on ? [{ ...m, ...data }, ...cur.filter((x) => x.id !== m.id)] : cur.filter((x) => x.id !== m.id)));
      toast.success(on ? "Pinned to top" : "Unpinned");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };
  const pinnedIds = new Set(pinned.map((p) => p.id));
  const del = async (m) => {
    try { await api.delete(`/team-messages/${m.id}`); setMsgs((cur) => cur.filter((x) => x.id !== m.id)); setPinned((cur) => cur.filter((x) => x.id !== m.id)); } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };
  return (
    <div className={`flex flex-col min-h-0 w-full ${className}`} data-testid="team-chat">
      {header !== undefined ? header : (
      <div className="flex items-center gap-3 mb-3">
        <ChatsCircle size={28} weight="duotone" className="text-brand-navy" />
        <div><h1 className="font-heading text-2xl font-bold text-brand-navy">Team Messages</h1>
          <p className="text-xs text-muted-foreground">{lt ? <>Messages linked to <b>{llabel || "this record"}</b> · <button type="button" className="underline" onClick={onShowAll} data-testid="team-show-all">show all</button></> : "One chat for everyone in the Hub — saved here for the record."}</p></div>
      </div>)}
      <PinnedBar pinned={pinned} onUnpin={(m) => pin(m, false)} />
      <div className="flex-1 overflow-y-auto border border-border bg-secondary/30 p-3 sm:p-4 space-y-3" data-testid="team-message-list">
        {!msgs.length && <div className="text-center text-sm text-muted-foreground py-10">No messages yet — say hello to the team.</div>}
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.user_id === user?.id} onDelete={del} reads={reads} isPinned={pinnedIds.has(m.id)} onPin={pin} />)}
        <div ref={endRef} />
      </div>
      <Composer key={`${lt}:${lid}`} replies={replies} onSent={(m) => add([m])} presetLink={lt ? { link_type: lt, link_id: lid, link_label: llabel } : null} />
    </div>
  );
}

export default function TeamMessages() {
  const [params, setParams] = useSearchParams();
  return (
    <div className="h-[calc(100dvh-8.5rem)] max-w-4xl mx-auto w-full flex" data-testid="team-messages-page">
      <TeamChat lt={params.get("link_type") || ""} lid={params.get("link_id") || ""} llabel={params.get("label") || ""} onShowAll={() => setParams({})} />
    </div>
  );
}
